import { AxiosRequestConfig } from "axios";
import apiClient from "./apiClient";

/**
 * An Error thrown by {@link apiRequest}. It behaves exactly like a regular
 * `Error` (same `message`), but additionally carries the HTTP status code when
 * the failure came from a server response. Callers that only read `message`
 * are unaffected; callers that need to distinguish, e.g. a 404 from a real
 * failure, can inspect `status`.
 */
export interface ApiError extends Error {
  /** HTTP status code from the response, when one was received. */
  status?: number;
}

export function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof Error &&
    typeof (error as ApiError).status === "number"
  );
}

export function isNotFoundError(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

function createApiError(message: string, status?: number): ApiError {
  const error = new Error(message) as ApiError;
  if (typeof status === "number") {
    error.status = status;
  }
  return error;
}

export async function apiRequest<T = any>(
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await apiClient(config);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      // FastAPI puts error text in `detail` (guard against the 422 array form);
      // other services use message/error.
      const data = error.response.data;
      const detail = typeof data?.detail === "string" ? data.detail : undefined;
      const message =
        detail ||
        data?.message ||
        data?.error ||
        "Request failed";
      throw createApiError(message, error.response.status);
    }
    if (error.request) {
      throw createApiError("Network error. Please try again");
    }
    throw createApiError(error.message || "Unknown error");
  }
}
